import { useRef, useState } from "react";
import type { PeriodoDashboard, ResumoDashboard } from "@base/contracts";
import { BarChart3, Download, RefreshCw } from "lucide-react";
import { EstadoDeErro, EstadoVazio, MolduraDoApp } from "@base/ui";
import { Button } from "@/components/ui/button";
import { PageLayout } from "@/components/layout/page-header";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { LogoRenkeRefs } from "../../../shared/branding/logo-renke-refs";
import { ErroDaApi } from "../../../shared/http/erro-da-api";
import { usePropsDoSeletorNaMoldura } from "../../../shared/tenant/projeto.contexto";
import { avisar } from "../../../shared/ui/dialogos-api";
import { useNavegacaoDoApp } from "../../referencias/presentation/navegacao-do-app";
import { rodapeDaSessaoNaLateral } from "../../referencias/presentation/rodape-da-sessao";
import {
  useColetarMetricasDashboard,
  useResumoDashboard,
  useTemDashboardGerir,
} from "../application/use-dashboard";
import { AbaOperacao } from "./aba-operacao";
import { AbaPerformance } from "./aba-performance";
import { AbaPublicacao } from "./aba-publicacao";
import { formatarDataCurta, mesAtualIso } from "./dashboard-formatadores";
import { exportarDashboardPdf } from "./exportar-dashboard-pdf";

type AbaDashboard = "operacao" | "performance" | "publicacao";

/**
 * THESIS: comando operacional do estúdio, não grade genérica de KPI.
 * OWN-WORLD: Renke (preto/neutros + amarelo evento), Swiss + editorial assimétrico.
 * STORY: Head vê o que trava hoje, onde a equipe satura e o que a rede entregou.
 * FIRST VIEWPORT: hero de ocupação + métricas compactas; abas underline; ações no topo.
 * FORM: Data-dense × Editorial (ui-ux-pro-max), traduzido para tokens Renke.
 * FINISH: unreviewed and undocumented is unfinished; this build ends with the finish review, the verdict, DESIGN.md, and every shipping raster carrying its provenance
 */
export function PaginaDashboard() {
  const seletor = usePropsDoSeletorNaMoldura();
  const navegacao = useNavegacaoDoApp("dashboard");
  const [periodo, definirPeriodo] = useState<PeriodoDashboard>("mes");
  const [aba, definirAba] = useState<AbaDashboard>("operacao");
  const refPainel = useRef<HTMLDivElement>(null);
  const resumo = useResumoDashboard({ periodo, mes: mesAtualIso() });
  const podeColetar = useTemDashboardGerir();
  const coletar = useColetarMetricasDashboard();

  const semDados =
    resumo.data &&
    resumo.data.produtividade.tempoCicloMedioDias === null &&
    resumo.data.performance.topPosts.length === 0 &&
    resumo.data.fidelidade.planejados === 0 &&
    Object.keys(resumo.data.publicacaoResumo.porStatus).length === 0;

  return (
    <MolduraDoApp
      nomeDoApp="Creative OS"
      marca={<LogoRenkeRefs />}
      titulo="Dashboard"
      subtitulo="O que precisa de atenção hoje"
      navegacao={navegacao}
      rodapeDaLateral={rodapeDaSessaoNaLateral}
      {...seletor}
      acoes={
        <div className="flex flex-wrap items-center gap-3">
          <Select
            value={periodo}
            onValueChange={(valor) =>
              definirPeriodo(valor as PeriodoDashboard)
            }
          >
            <SelectTrigger
              className="h-9 w-[8.5rem] cursor-pointer bg-background"
              aria-label="Período do dashboard"
            >
              <SelectValue placeholder="Período" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="semana">Semana</SelectItem>
              <SelectItem value="mes">Mês</SelectItem>
              <SelectItem value="30d">30 dias</SelectItem>
            </SelectContent>
          </Select>
          {podeColetar ? (
            <Button
              variant="outline"
              className="cursor-pointer"
              disabled={coletar.isPending}
              onClick={() => {
                void coletar
                  .mutateAsync()
                  .then((resultado) =>
                    avisar({
                      titulo:
                        resultado.coletados === 0
                          ? "Nenhum post processado"
                          : "Coleta concluída",
                      descricao:
                        resultado.coletados === 0
                          ? "Não havia posts publicados nos últimos 30 dias para puxar números."
                          : `${resultado.coletados} post(s) atualizado(s).`,
                    }),
                  )
                  .catch((erro: unknown) =>
                    avisar({
                      titulo: "Não foi possível coletar",
                      descricao:
                        erro instanceof Error
                          ? erro.message
                          : "Tente de novo em instantes.",
                    }),
                  );
              }}
              aria-label="Coletar métricas do Instagram agora"
            >
              <RefreshCw
                className={`size-4 ${coletar.isPending ? "motion-safe:animate-spin" : ""}`}
                aria-hidden="true"
              />
              Coletar agora
            </Button>
          ) : null}
          <Button
            variant="outline"
            className="cursor-pointer"
            onClick={() => void exportarDashboardPdf(refPainel.current)}
            aria-label="Exportar aba ativa em PDF"
          >
            <Download className="size-4" aria-hidden="true" />
            Exportar PDF
          </Button>
        </div>
      }
    >
      <PageLayout>
        <div
          ref={refPainel}
          data-vazio={semDados ? "true" : "false"}
          className="dashboard-atmosfera flex w-full flex-col gap-8 animate-aparecer print:gap-4"
          id="painel-dashboard"
        >
          <div className="mb-2 hidden items-center gap-4 border-b border-border pb-4 print:flex">
            <LogoRenkeRefs />
            <div className="space-y-1">
              <p className="text-lg font-semibold tracking-tight">
                Dashboard · Creative OS
              </p>
              <p className="text-sm text-muted-foreground">
                {formatarDataCurta(new Date().toISOString())}
              </p>
            </div>
          </div>
          {resumo.isPending ? <EsqueletoDashboard /> : null}

          {resumo.isError ? (
            <EstadoDeErro
              titulo="Não foi possível carregar o dashboard"
              mensagem={
                resumo.error instanceof ErroDaApi && resumo.error.status === 403
                  ? "Você precisa de permissão para ver o dashboard neste workspace."
                  : resumo.error instanceof Error
                    ? resumo.error.message
                    : "Erro inesperado."
              }
              aoTentarNovamente={() => void resumo.refetch()}
            />
          ) : null}

          {resumo.data && semDados ? (
            <EstadoVazio
              icone={<BarChart3 className="size-8" aria-hidden="true" />}
              titulo="Sem dados suficientes"
              descricao="Publique conteúdos ou conecte Facebook e Instagram do cliente para ver métricas aqui."
            />
          ) : null}

          {resumo.data && !semDados ? (
            <ConteudoComAbas
              resumo={resumo.data}
              aba={aba}
              aoTrocarAba={definirAba}
            />
          ) : null}
        </div>
      </PageLayout>
    </MolduraDoApp>
  );
}

function ConteudoComAbas({
  resumo,
  aba,
  aoTrocarAba,
}: {
  readonly resumo: ResumoDashboard;
  readonly aba: AbaDashboard;
  readonly aoTrocarAba: (aba: AbaDashboard) => void;
}) {
  return (
    <Tabs
      value={aba}
      onValueChange={(v) => aoTrocarAba(v as AbaDashboard)}
      className="w-full gap-8"
    >
      <TabsList className="print:hidden">
        <TabsTrigger
          value="operacao"
          className="px-5"
        >
          Operação
        </TabsTrigger>
        <TabsTrigger
          value="performance"
          className="px-5"
        >
          Performance
        </TabsTrigger>
        <TabsTrigger
          value="publicacao"
          className="px-5"
        >
          Publicação
          {resumo.publicacaoResumo.totalFalhas > 0 ? (
            <span className="ml-2 inline-flex min-w-5 items-center justify-center rounded-full bg-destructive px-1.5 py-0.5 text-[10px] font-semibold text-destructive-foreground">
              {resumo.publicacaoResumo.totalFalhas}
            </span>
          ) : null}
        </TabsTrigger>
      </TabsList>
      <TabsContent value="operacao" className="mt-0 outline-none">
        <AbaOperacao resumo={resumo} />
      </TabsContent>
      <TabsContent value="performance" className="mt-0 outline-none">
        <AbaPerformance resumo={resumo} />
      </TabsContent>
      <TabsContent value="publicacao" className="mt-0 outline-none">
        <AbaPublicacao resumo={resumo} />
      </TabsContent>
    </Tabs>
  );
}

function EsqueletoDashboard() {
  return (
    <div
      className="space-y-8"
      aria-busy="true"
      aria-label="Carregando dashboard"
    >
      <Skeleton className="h-10 w-80 rounded-none" />
      <Skeleton className="h-[18rem] w-full rounded-[1rem]" />
      <div className="grid gap-6 lg:grid-cols-12">
        <Skeleton className="h-52 w-full rounded-[1rem] lg:col-span-7" />
        <Skeleton className="h-52 w-full rounded-[1rem] lg:col-span-5" />
      </div>
    </div>
  );
}
